import { useUser } from "@clerk/tanstack-react-start";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  ClipboardCheck,
  LockKeyhole,
  MessageCircle,
  Plus,
  Target,
  Trophy,
  UserRound,
  X,
} from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EncryptionGate } from "~/components/encryption-gate";
import { useAppForm } from "~/components/form/app-form";
import { FieldErrorsProvider } from "~/components/form/fields/field-errors";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/cn";
import { useEncryptOnSave, useUnlockedKey } from "~/lib/crypto";
import { markEngagementOnboardedFn } from "~/lib/functions/engagement-onboarding";
import { proposeGoalFn } from "~/lib/functions/goal-proposal";
import { useEngagementCounterpart } from "~/lib/queries/encryptedDataQuery";
import { useCoachInfoForEngagement } from "~/lib/queries/engagementQuery";
import { linkGoalWithCoachKeys } from "~/lib/queries/goalLinkQuery";
import { useUpdateUserProfile } from "~/lib/queries/userQuery";
import { UserProfileSchema } from "~/lib/schemas/user-auth";
import { getInitials } from "~/lib/utils";
import { formatDateOnly } from "~/lib/utils/timezone";

import { LifeDesignGoalPicker } from "./-components/life-design-goal-picker";

export const Route = createFileRoute("/_user/coaching/$engagementId_/welcome")({
  staticData: { title: "Welcome" },
  component: WelcomeWizard,
});

const MAX_INITIAL_GOALS = 3;

const NameStepSchema = UserProfileSchema.pick({
  firstName: true,
  lastName: true,
});

// The wizard renders inside the _user shell, so the available height is the
// viewport minus the 56px (h-14) topbar and the shell's vertical padding
// (py-4 / md:py-6). Sizing to that region keeps `justify-center` centering the
// content within the visible area instead of overflowing past the fold.
const WIZARD_CONTAINER_CLASS =
  "mx-auto flex min-h-[calc(100dvh-5.5rem)] w-full max-w-xl flex-col justify-center gap-10 px-4 py-14 md:min-h-[calc(100dvh-6.5rem)]";

type StepKey = "name" | "intro" | "encryption" | "goals";

/** Wizard-shaped placeholder (step dots, avatar, title, list, button) for the first load. */
function WizardSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading your welcome"
      className={WIZARD_CONTAINER_CLASS}
    >
      <div className="flex items-center justify-center gap-2">
        <Skeleton className="h-2 w-6 rounded-full" />
        <Skeleton className="size-2 rounded-full" />
        <Skeleton className="size-2 rounded-full" />
      </div>
      <div className="flex flex-col items-center space-y-8">
        <Skeleton className="size-20 rounded-full" />
        <div className="flex w-full flex-col items-center space-y-3">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-9 w-3/4" />
        </div>
        <div className="w-full max-w-sm space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
        <Skeleton className="h-16 w-56 rounded-full" />
      </div>
    </div>
  );
}

function StepDots({ count, current }: { count: number; current: number }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className={cn(
            "h-2 rounded-full transition-[width,background-color] duration-200",
            i === current ? "w-6 bg-purple-600" : "w-2 bg-gray-200",
          )}
        />
      ))}
    </div>
  );
}

/**
 * Post-invitation welcome ceremony: set a name (for invitees whose account was
 * auto-created without one), meet the program, set up encryption, and (when the
 * coach asked for it) share initial goals — which enter as proposals the coach
 * reviews. A ticked Life Design goal is linked to the program, not copied. Renders inside the workspace shell, sized to the height below the
 * topbar so the stepper stays vertically centered.
 */
function WelcomeWizard() {
  const { engagementId } = Route.useParams();
  const navigate = useNavigate();
  const keyManager = useUnlockedKey();
  const { encryptFields } = useEncryptOnSave(keyManager);

  const { user, isLoaded } = useUser();
  const coachInfoQuery = useCoachInfoForEngagement(engagementId);
  const { data: coachInfo } = coachInfoQuery;
  // `getCoachInfoForEngagementFn` scopes its lookup to the signed-in user's own
  // engagements and *resolves with null* when there's no match — a missing,
  // deleted, or someone else's engagement is an expected miss, not a query
  // error. So an inaccessible invite has to be detected on the data, not just
  // on `isError` (which only covers an actual request failure).
  const engagementInaccessible =
    coachInfoQuery.isError || (coachInfoQuery.isSuccess && !coachInfo);
  const { data: counterpart } = useEngagementCounterpart(engagementId, {
    enabled: coachInfoQuery.isSuccess && !!coachInfo,
  });

  const [step, setStep] = useState(0);
  const [goalDrafts, setGoalDrafts] = useState<string[]>([""]);
  const [pickedGoalIds, setPickedGoalIds] = useState<string[]>([]);
  const [finishing, setFinishing] = useState(false);

  // Decide whether to collect the name once, when Clerk first loads. Freezing
  // it keeps the step list stable even after the profile update refreshes the
  // Clerk user mid-flow (otherwise the steps would re-index under us).
  const [needsName, setNeedsName] = useState<boolean | null>(null);

  const includeGoalsStep = coachInfo?.onboardingGoalsRequested ?? false;
  const coachFirstName = coachInfo?.name?.split(" ")[0] ?? "your coach";

  const steps: StepKey[] = [
    ...(needsName ? (["name"] as const) : []),
    "intro",
    "encryption",
    ...(includeGoalsStep ? (["goals"] as const) : []),
  ];
  const currentKey = steps[step];
  const filledDrafts = goalDrafts.filter((d) => d.trim()).length;
  const goNext = () => setStep((s) => Math.min(s + 1, steps.length - 1));

  const updateProfile = useUpdateUserProfile();
  const nameForm = useAppForm({
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    },
  });
  const nameValues = useStore(nameForm.store, (s) => s.values);

  useEffect(() => {
    if (isLoaded && needsName === null) {
      setNeedsName(!user?.firstName?.trim() || !user?.lastName?.trim());
      // Clerk resolves after the form is created, so seed it once loaded to
      // prefill any name the user already has (e.g. first name but no last).
      nameForm.reset({
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
      });
    }
  }, [isLoaded, user, needsName, nameForm]);

  const [nameAttempted, setNameAttempted] = useState(false);
  const nameParsed = NameStepSchema.safeParse(nameValues);
  const nameErrorFor = (field: string): string | undefined => {
    if (!nameAttempted || nameParsed.success) return undefined;
    return nameParsed.error.issues.find((i) => i.path[0] === field)?.message;
  };

  const saveName = async () => {
    if (updateProfile.isPending) return;
    setNameAttempted(true);
    if (!nameParsed.success) return;
    try {
      await updateProfile.mutateAsync({
        firstName: nameParsed.data.firstName,
        lastName: nameParsed.data.lastName,
        email: user?.primaryEmailAddress?.emailAddress ?? "",
        clerkUserId: user?.id,
      });
      goNext();
    } catch (error) {
      console.error("Failed to save user name during onboarding:", error);
      toast.error("Couldn't save your name. Please try again.");
    }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      const drafts = goalDrafts.map((d) => d.trim()).filter(Boolean);
      const picked = includeGoalsStep ? pickedGoalIds : [];
      if (includeGoalsStep && drafts.length + picked.length > 0 && keyManager) {
        // Each goal leaves the step once it is shared, so a retry after a
        // failure shares only what is left.
        for (const goalId of picked) {
          await linkGoalWithCoachKeys(keyManager, { goalId, engagementId });
          setPickedGoalIds((prev) => prev.filter((id) => id !== goalId));
        }
        for (const draft of drafts) {
          // Each goal is saved with its keys, the coach's included.
          const encrypted = await encryptFields(
            { description: draft },
            counterpart ? [counterpart] : [],
          );
          await proposeGoalFn({
            data: {
              engagementId,
              encryptedDescription: encrypted.fields.description,
              iv: encrypted.iv,
              keyGrants: encrypted.keyGrants,
            },
          });
          setGoalDrafts((prev) => {
            const next = prev.filter(
              (_, index) => index !== prev.findIndex((d) => d.trim() === draft),
            );
            return next.length > 0 ? next : [""];
          });
        }
        toast.success(
          `Shared with ${coachFirstName} — they'll review your goals`,
        );
      }
      await markEngagementOnboardedFn({ data: { engagementId } });
      navigate({ to: "/coaching/$engagementId", params: { engagementId } });
    } catch {
      toast.error("Something went wrong finishing setup. Please try again.");
      setFinishing(false);
    }
  };

  if (needsName === null || coachInfoQuery.isPending) {
    return <WizardSkeleton />;
  }

  if (engagementInaccessible) {
    return (
      <div className={WIZARD_CONTAINER_CLASS}>
        <div className="space-y-6 text-center">
          <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-gray-100">
            <X className="size-6 text-gray-500" />
          </div>
          <div className="space-y-3">
            <h1 className="font-heading text-3xl font-medium tracking-tight text-gray-900">
              This invite isn&apos;t valid
            </h1>
            <p className="mx-auto max-w-md text-sm leading-relaxed text-gray-600">
              We couldn&apos;t find a coaching program for this link. It may
              have expired, already been used, or belong to a different account.
            </p>
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate({ to: "/dashboard" })}
          >
            Go to your dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={WIZARD_CONTAINER_CLASS}>
      <StepDots count={steps.length} current={step} />

      {currentKey === "name" && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 text-center"
        >
          <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-purple-50">
            <UserRound className="size-6 text-purple-600" />
          </div>
          <div className="space-y-3">
            <h1 className="font-heading text-3xl font-medium tracking-tight text-gray-900">
              What should we call you?
            </h1>
            <p className="mx-auto max-w-md text-sm leading-relaxed text-gray-600">
              Add your name so {coachFirstName} and your workspace know who you
              are.
            </p>
          </div>
          <FieldErrorsProvider value={nameErrorFor}>
            <form
              className="mx-auto max-w-sm space-y-4 text-left"
              onSubmit={(e) => {
                e.preventDefault();
                void saveName();
              }}
            >
              <nameForm.AppField name="firstName">
                {(f) => (
                  <f.TextField
                    label="First name"
                    placeholder="Your first name"
                  />
                )}
              </nameForm.AppField>
              <nameForm.AppField name="lastName">
                {(f) => (
                  <f.TextField label="Last name" placeholder="Your last name" />
                )}
              </nameForm.AppField>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={updateProfile.isPending}
                className="w-full"
              >
                Continue
                <ArrowRight className="ml-2 size-4" />
              </Button>
            </form>
          </FieldErrorsProvider>
        </motion.div>
      )}

      {currentKey === "intro" && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 text-center"
        >
          <Avatar className="mx-auto size-20">
            <AvatarImage src={coachInfo?.profilePictureUrl || ""} />
            <AvatarFallback className="text-xl">
              {getInitials([coachInfo?.name])}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-3">
            <p className="text-xs font-medium tracking-[0.22em] text-gray-400 uppercase">
              Your coaching program
            </p>
            <h1 className="font-heading text-3xl font-medium tracking-tight text-gray-900">
              You&apos;re working with {coachInfo?.name ?? "your coach"}
            </h1>
            {coachInfo?.dateStart && coachInfo?.dateEnd && (
              <p className="text-sm text-gray-500">
                {formatDateOnly(coachInfo.dateStart)} &ndash;{" "}
                {formatDateOnly(coachInfo.dateEnd)}
              </p>
            )}
          </div>
          <ul className="mx-auto max-w-sm space-y-4 text-left">
            <li className="flex items-start gap-3 text-sm text-gray-600">
              <CalendarCheck className="mt-0.5 size-4 shrink-0 text-purple-600" />
              Sessions, recaps, and the actions you agree on live in one
              workspace.
            </li>
            <li className="flex items-start gap-3 text-sm text-gray-600">
              <ClipboardCheck className="mt-0.5 size-4 shrink-0 text-purple-600" />
              Before each session, a two-minute check-in helps you both make it
              count.
            </li>
            <li className="flex items-start gap-3 text-sm text-gray-600">
              <Trophy className="mt-0.5 size-4 shrink-0 text-purple-600" />
              Log wins between sessions — progress worth celebrating.
            </li>
            <li className="flex items-start gap-3 text-sm text-gray-600">
              <MessageCircle className="mt-0.5 size-4 shrink-0 text-purple-600" />
              Message {coachFirstName} right from your workspace.
            </li>
          </ul>
          <Button variant="primary" size="lg" onClick={goNext}>
            Let&apos;s set things up
            <ArrowRight className="ml-2 size-4" />
          </Button>
        </motion.div>
      )}

      {currentKey === "encryption" && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 text-center"
        >
          <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-purple-50">
            <LockKeyhole className="size-6 text-purple-600" />
          </div>
          <div className="space-y-3">
            <h1 className="font-heading text-3xl font-medium tracking-tight text-gray-900">
              Private by design
            </h1>
            <p className="mx-auto max-w-md text-sm leading-relaxed text-gray-600">
              Your notes, goals and check-ins are encrypted with a PIN only you
              know before they are stored, and you choose what {coachFirstName}{" "}
              can read. NOVA, our AI, can read them only when {coachFirstName}{" "}
              turns it on for your program, and every read is logged.
            </p>
          </div>
          {/* The gate walks the user through PIN setup, then renders the ✓ */}
          <EncryptionGate
            fallback={
              <div className="flex flex-col items-center space-y-6">
                <Skeleton className="h-5 w-64" />
                <Skeleton className="h-16 w-56 rounded-full" />
              </div>
            }
          >
            <div className="space-y-6">
              <p className="inline-flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle2 className="size-4" />
                Your encryption is set up and unlocked
              </p>
              <div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => (includeGoalsStep ? goNext() : void finish())}
                  loading={!includeGoalsStep && finishing}
                >
                  {includeGoalsStep ? (
                    <>
                      Continue
                      <ArrowRight className="ml-2 size-4" />
                    </>
                  ) : (
                    "Open your workspace"
                  )}
                </Button>
              </div>
            </div>
          </EncryptionGate>
        </motion.div>
      )}

      {currentKey === "goals" && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="space-y-3 text-center">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-purple-50">
              <Target className="size-6 text-purple-600" />
            </div>
            <h1 className="font-heading text-3xl font-medium tracking-tight text-gray-900">
              What do you want to work toward?
            </h1>
            <p className="mx-auto max-w-md text-sm leading-relaxed text-gray-600">
              {coachFirstName} asked new clients to share a starting point.
              Don&apos;t overthink it — these become suggestions{" "}
              {coachFirstName} reviews, and you&apos;ll shape them together.
            </p>
          </div>

          <LifeDesignGoalPicker
            keyManager={keyManager}
            coachFirstName={coachFirstName}
            picked={pickedGoalIds}
            canPickMore={
              pickedGoalIds.length + filledDrafts < MAX_INITIAL_GOALS
            }
            onPickedChange={setPickedGoalIds}
            onUnlock={() => setStep(steps.indexOf("encryption"))}
          />

          <div className="space-y-3">
            {goalDrafts.map((draft, i) => (
              <div key={i} className="flex items-start gap-2">
                <Textarea
                  value={draft}
                  onChange={(e) =>
                    setGoalDrafts((prev) =>
                      prev.map((d, j) => (j === i ? e.target.value : d)),
                    )
                  }
                  placeholder={
                    i === 0
                      ? "e.g. Lead my team with more confidence"
                      : "Another goal (optional)"
                  }
                  // The step takes up to MAX_INITIAL_GOALS goals in all.
                  disabled={
                    !draft.trim() &&
                    pickedGoalIds.length + filledDrafts >= MAX_INITIAL_GOALS
                  }
                  className="min-h-[60px] flex-1 resize-none"
                />
                {goalDrafts.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove goal"
                    onClick={() =>
                      setGoalDrafts((prev) => prev.filter((_, j) => j !== i))
                    }
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>
            ))}
            {goalDrafts.length + pickedGoalIds.length < MAX_INITIAL_GOALS && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                onClick={() => setGoalDrafts((prev) => [...prev, ""])}
              >
                <Plus className="size-4" />
                Add another
              </Button>
            )}
          </div>

          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" disabled={finishing} onClick={finish}>
              Skip for now
            </Button>
            <Button
              variant="primary"
              size="lg"
              loading={finishing}
              disabled={
                (filledDrafts === 0 && pickedGoalIds.length === 0) ||
                !keyManager
              }
              onClick={finish}
            >
              Share &amp; open your workspace
            </Button>
          </div>
          {!keyManager && (
            <p className="text-center text-xs text-amber-600">
              Unlock your encryption key (previous step) to share goals.
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
}
